import React, { useEffect, useRef, useState } from 'react';

import { downloadCSVApi } from 'api/analytics/review';
import FileDownload from 'components/FileDownload';
import FileDownloadPopper from 'components/FileDownloadPopper';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { StyledTooltip } from 'styles/common';

import { CSV_DOWNLOAD_API_POLL_INTERVAL, CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL } from 'constants/ProjectConstants';

import { getStatusOfRequestIds as getStatusOfRequestIdsReview } from 'services/analytics/review';
import { showSuccessSnackbar } from 'services/snackbar';

import { DownloadCSVButton } from '../commonComponents';

const DEFAULT_FILE_NAME = 'TabularData.csv';

const DownloadCSVData = ({
  localStorageKey,
  generateCSVData,
  downloadText,
  getStatusOfRequestIds = getStatusOfRequestIdsReview,
  showSuccessToast = false,
  toastMessage = '',
  disabled = false,
  tooltipText = '',
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [downloadBlob, setDownloadBlob] = useState(null);

  const [hasError, setHasError] = useState(false);

  const dispatch = useDispatch();

  const getDownloadRequestFromLS = () => {
    try {
      const downloadRequests = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];
      return downloadRequests;
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const [downloadRequestInLS, setDownloadRequestsInLS] = useState(getDownloadRequestFromLS);

  const handleClick = () => {
    if (disabled) return;
    setIsOpen(true);
    setHasError(false);
  };

  const onSuccess = (blob, openPopper = false) => {
    setDownloadBlob(blob);
    setIsFetching(false);
    if (openPopper) {
      setIsOpen(true);
    }
  };

  const onError = (error) => {
    setDownloadBlob(null);
    setIsFetching(false);
    setIsOpen(true);
    setHasError(true);
    console.error('Error in CSV download : ', error);
  };

  const onDownloadComplete = (requestId) => {
    setDownloadBlob(null);
    if (showSuccessToast) {
      dispatch(showSuccessSnackbar(toastMessage, 4000));
    }

    try {
      const prevGeneratedFiles = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      const generatedFiles = prevGeneratedFiles.filter((file) => file.uuid !== requestId);

      const stringifiedGeneratedFiles = JSON.stringify(generatedFiles);

      window.localStorage.setItem(localStorageKey, stringifiedGeneratedFiles);
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const componentEle = useRef(null);

  const generateFile = () => {
    setIsFetching(true);
    return generateCSVData();
  };

  const downloadCSVBlob = async (requestId) => {
    try {
      const response = await downloadCSVApi(requestId);
      const contentDispositionString = response.headers.get('content-disposition');
      const fileName = contentDispositionString.split('filename=')?.[1]?.split(';')?.[0] || fileName;
      const fetchedBlob = await response.blob();
      onSuccess({ fetchedBlob, fileName, requestId }, true);
    } catch (err) {
      console.error('Error in downloading CSV ', err);
    }
  };

  const apiPoll = async (fetchFunction, functionCondition, pollingInterval) => {
    try {
      let result = await fetchFunction();
      while (functionCondition(result)) {
        await wait(pollingInterval);
        result = await fetchFunction();
      }
      if (Array.isArray(result)) {
        result.forEach((result) => {
          downloadCSVBlob(result.uuid);
        });
      }
    } catch (err) {
      console.error('Error in fetching status of CSV ', err);
    }
  };

  const wait = (ms = 1000) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const validateStatusResponse = (res = []) =>
    res
      .map((report) => {
        switch (report.status) {
          case 'IN_PROGRESS':
            return true;
          case 'FINISHED':
            return false;
          case 'ERROR': {
            onError('Error in generating file');
            return false;
          }
          default:
            onError('Error in generating file');

            return false;
        }
      })
      .reduce((prev, curr) => prev + curr, false);

  const getStatusForRequestIds = async () => {
    try {
      const downloadRequests = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      if (downloadRequests.length) {
        const currentTS = Date.now();

        // files which were requested CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL back are deleted from LS
        const validDownloadRequests = downloadRequests.filter(
          (request) => currentTS - request.requestedTS <= CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL
        );

        window.localStorage.setItem(localStorageKey, JSON.stringify(validDownloadRequests ?? []));

        validDownloadRequests.forEach((request) => {
          setIsFetching(true);
        });

        const response = await getStatusOfRequestIds(validDownloadRequests);
        return response;
      }
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  useEffect(() => {
    apiPoll(getStatusForRequestIds, validateStatusResponse, CSV_DOWNLOAD_API_POLL_INTERVAL);
  }, [downloadRequestInLS]);

  const handleClickAway = () => {
    setIsOpen(false);
  };

  return (
    <StyledFileDownload onClick={handleClick} ref={componentEle}>
      <StyledTooltip title={tooltipText}>
        <div>
          <DownloadCSVButton
            isOpen={isOpen}
            isFetching={isFetching}
            hasError={hasError}
            downloadBlob={downloadBlob}
            disabled={disabled}
          />
        </div>
      </StyledTooltip>

      {isOpen ? (
        <FileDownloadPopper
          handleClickAway={handleClickAway}
          open={isOpen}
          ref={componentEle}
          fileName={DEFAULT_FILE_NAME}
          requestData={generateFile}
          onSuccess={onSuccess}
          onError={onError}
          onDownloadComplete={onDownloadComplete}
          isFetching={isFetching}
          downloadBlob={downloadBlob}
          hasError={hasError}
          setDownloadRequestsInLS={setDownloadRequestsInLS}
          downloadText={downloadText}
          localStorageKey={localStorageKey}
        />
      ) : null}
    </StyledFileDownload>
  );
};

const StyledFileDownload = styled(FileDownload)`
  margin-left: auto;
`;

export default DownloadCSVData;
